import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import qs from 'query-string';
import { dataCentralApi } from '@/api/client';
import { customerKeys } from '@/api/customers';
import type { ResponseArray, ResponseObject } from '@/types/shared';

/**
 * Customer "Basic Information" surface — contacts, address and the row-action
 * mutations (add note / add contact / convert / delete / grey-market). Endpoints,
 * payload field names and envelope shapes are lifted verbatim from the
 * DataCentral v4 backend (`modules/Customer/services`) so the API is hit
 * unchanged.
 */

/* ── Types ─────────────────────────────────────────────────────────────── */

/** A row from `GET /customers/:id/contacts`. Fields mirror the backend record. */
export interface CustomerContact {
  customer_contact_id: number;
  customer_id: number;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  contact_address?: string | null;
  contact_note?: string | null;
  contact_position?: string;
  contact_industry?: string;
  contact_source?: string;
  contact_service_interestings?: string[];
  contact_sale_followers?: number[];
  contact_cs_followers?: number[];
  contact_label?: string[];
  contact_roles?: string[];
  contact_language?: string;
  contact_status?: string;
  /** True when this is the customer's primary contact. */
  contact_firstable?: boolean;
  contact_ordered?: number;
  created_at?: string;
  updated_at?: string;
}

/** `GET /customers/:id/address` → `{ result }`. */
export interface CustomerAddress {
  customer_country?: string;
  customer_province?: string;
  customer_district?: string;
  customer_address?: string;
  customer_zipcode?: string;
}

/** `POST /customers/:id/notes` payload — the note is auto-pinned by default. */
export interface AddNotePayload {
  note_description: string;
  is_pinned: boolean;
}

/** `POST /customers/:id/contacts` payload — a partial contact record. */
export type AddContactPayload = Partial<CustomerContact>;

/** `PUT /customers/:id/contacts/:cid` payload — an editable subset of a contact. */
export type UpdateContactPayload = Partial<CustomerContact>;

/** How a linked account is handled when a contact is deleted. */
export type ContactDeleteAuthAction = 'keep' | 'unlink' | 'delete';

/** `DELETE /customers/:id/contacts/:cid` body. */
export interface DeleteContactPayload {
  message: string;
  auth_action?: ContactDeleteAuthAction;
}

/**
 * `GET /partner/auth/account/status?contact_id=` — Keycloak account state for a
 * contact. Best-effort; the endpoint may be unavailable (404) in some envs.
 */
export interface ContactAccountStatus {
  has_account?: boolean;
  authentication_method?: string;
  keycloak_status?: { enabled?: boolean };
}

/** `GET /partner/auth/impersonate/whois?contact_id=` — can-impersonate probe. */
export interface ContactWhois {
  valid?: boolean;
  authentication_method?: string;
}

/** `POST /partner/auth/account/create` body. */
export interface CreateContactAccountPayload {
  password: string;
  password_confirmation: string;
  send_welcome_email: boolean;
}

/** `POST /partner/auth/impersonate` result — carries the token for the SSO hop. */
export interface ImpersonateResult {
  access_token?: string;
  uuid?: string;
  exp?: number;
  auth_method?: string;
}

/* ── Query keys ────────────────────────────────────────────────────────── */

export const basicKeys = {
  contacts: (id: number) => ['customers', 'contacts', id] as const,
  contact: (id: number, contactId: number) =>
    ['customers', 'contacts', id, contactId] as const,
  address: (id: number) => ['customers', 'address', id] as const,
  accountStatus: (contactId: number) =>
    ['customers', 'contact-account-status', contactId] as const,
  whois: (contactId: number) => ['customers', 'contact-whois', contactId] as const,
};

/* ── Reads ─────────────────────────────────────────────────────────────── */

/** `GET /customers/:id/contacts` — the customer's contact list. */
export function useCustomerContacts(
  id: number,
  params: { page?: number; page_size?: number } = {},
) {
  return useQuery({
    queryKey: [...basicKeys.contacts(id), params],
    enabled: !!id,
    queryFn: async (): Promise<ResponseArray<CustomerContact>> => {
      const query = qs.stringify({ page: 1, page_size: 50, ...params });
      const res = await dataCentralApi.get<ResponseArray<CustomerContact>>(
        `/customers/${id}/contacts?${query}`,
      );
      return res.data;
    },
  });
}

/** `GET /customers/:id/address` — the customer's postal address. */
export function useCustomerAddress(id: number) {
  return useQuery({
    queryKey: basicKeys.address(id),
    enabled: !!id,
    queryFn: async (): Promise<ResponseObject<CustomerAddress>> => {
      const res = await dataCentralApi.get<ResponseObject<CustomerAddress>>(
        `/customers/${id}/address`,
      );
      return res.data;
    },
  });
}

/** `GET /customers/:id/contacts/:cid` — a single contact record. */
export function useContactDetail(id: number, contactId?: number) {
  return useQuery({
    queryKey: basicKeys.contact(id, contactId ?? 0),
    enabled: !!id && !!contactId,
    queryFn: async (): Promise<ResponseObject<CustomerContact>> => {
      const res = await dataCentralApi.get<ResponseObject<CustomerContact>>(
        `/customers/${id}/contacts/${contactId}`,
      );
      return res.data;
    },
  });
}

/**
 * `GET /partner/auth/account/status?contact_id=` — Keycloak account state.
 * Best-effort: `retry: false` and errors are swallowed by the caller so a
 * missing/unavailable partner-auth service just hides the account block.
 */
export function useContactAccountStatus(contactId?: number) {
  return useQuery({
    queryKey: basicKeys.accountStatus(contactId ?? 0),
    enabled: !!contactId,
    retry: false,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<ContactAccountStatus> => {
      const res = await dataCentralApi.get<ContactAccountStatus>(
        `/partner/auth/account/status?contact_id=${contactId}`,
      );
      return res.data;
    },
  });
}

/** `GET /partner/auth/impersonate/whois?contact_id=` — can-impersonate probe. */
export function useContactWhois(contactId?: number) {
  return useQuery({
    queryKey: basicKeys.whois(contactId ?? 0),
    enabled: !!contactId,
    retry: false,
    staleTime: 15 * 60 * 1000,
    queryFn: async (): Promise<ContactWhois> => {
      const res = await dataCentralApi.get<ContactWhois>(
        `/partner/auth/impersonate/whois?contact_id=${contactId}`,
      );
      return res.data;
    },
  });
}

/* ── Mutations ─────────────────────────────────────────────────────────── */

/** `POST /customers/:id/notes` — add a customer note; invalidates activity. */
export function useAddCustomerNote(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: AddNotePayload) => {
      const res = await dataCentralApi.post<ResponseObject<unknown>>(
        `/customers/${id}/notes`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customers', 'activity-logs', id] });
      qc.invalidateQueries({ queryKey: ['customers', 'logs', id] });
    },
  });
}

/** `POST /customers/:id/contacts` — add a contact; invalidates the contact list. */
export function useAddCustomerContact(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: AddContactPayload) => {
      const res = await dataCentralApi.post<ResponseObject<CustomerContact>>(
        `/customers/${id}/contacts`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: basicKeys.contacts(id) });
    },
  });
}

/** `PUT /customers/:id/contacts/:cid` — edit a contact; invalidates the list + record. */
export function useUpdateContact(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      contactId: number;
      payload: UpdateContactPayload;
    }) => {
      const res = await dataCentralApi.put<ResponseObject<CustomerContact>>(
        `/customers/${id}/contacts/${vars.contactId}`,
        vars.payload,
      );
      return res.data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: basicKeys.contacts(id) });
      qc.invalidateQueries({ queryKey: basicKeys.contact(id, vars.contactId) });
    },
  });
}

/** `DELETE /customers/:id/contacts/:cid` — remove a contact with a reason + auth action. */
export function useDeleteContact(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      contactId: number;
      payload: DeleteContactPayload;
    }) => {
      const res = await dataCentralApi.delete<ResponseObject<unknown>>(
        `/customers/${id}/contacts/${vars.contactId}`,
        { data: vars.payload },
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: basicKeys.contacts(id) });
    },
  });
}

/** `PUT /customers/:id/contacts/:cid/set-primary` — promote a contact to primary. */
export function useSetPrimaryContact(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (contactId: number) => {
      const res = await dataCentralApi.put<ResponseObject<unknown>>(
        `/customers/${id}/contacts/${contactId}/set-primary`,
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: basicKeys.contacts(id) });
    },
  });
}

/* ── Contact account lifecycle (Keycloak via /partner/auth/*) ──────────────
 * Best-effort: the partner-auth service may be unavailable in some envs. The
 * UI guards these behind the account-status query and hides the block on error.
 * On success each invalidates the account-status + whois reads for the contact.
 */

/** Invalidate the account-status + whois reads for one contact. */
function invalidateContactAccount(qc: ReturnType<typeof useQueryClient>, contactId: number) {
  qc.invalidateQueries({ queryKey: basicKeys.accountStatus(contactId) });
  qc.invalidateQueries({ queryKey: basicKeys.whois(contactId) });
}

/** `POST /partner/auth/account/create` — create a Keycloak account for the contact. */
export function useCreateContactAccount(contactId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateContactAccountPayload) => {
      const res = await dataCentralApi.post<ResponseObject<unknown>>(
        `/partner/auth/account/create`,
        { contact_id: contactId, ...payload },
      );
      return res.data;
    },
    onSuccess: () => invalidateContactAccount(qc, contactId),
  });
}

/** `POST /partner/auth/account/sync` — re-sync the linked account. */
export function useSyncContactAccount(contactId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await dataCentralApi.post<ResponseObject<unknown>>(
        `/partner/auth/account/sync`,
        { contact_id: contactId },
      );
      return res.data;
    },
    onSuccess: () => invalidateContactAccount(qc, contactId),
  });
}

/** `POST /partner/auth/account/unlink` — unlink the account from the contact. */
export function useUnlinkContactAccount(contactId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await dataCentralApi.post<ResponseObject<unknown>>(
        `/partner/auth/account/unlink`,
        { contact_id: contactId },
      );
      return res.data;
    },
    onSuccess: () => invalidateContactAccount(qc, contactId),
  });
}

/** `POST /partner/auth/account/enable` — re-enable a disabled account. */
export function useEnableContactAccount(contactId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (reason?: string) => {
      const res = await dataCentralApi.post<ResponseObject<unknown>>(
        `/partner/auth/account/enable`,
        { contact_id: contactId, reason },
      );
      return res.data;
    },
    onSuccess: () => invalidateContactAccount(qc, contactId),
  });
}

/** `POST /partner/auth/account/disable` — disable the linked account. */
export function useDisableContactAccount(contactId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (reason?: string) => {
      const res = await dataCentralApi.post<ResponseObject<unknown>>(
        `/partner/auth/account/disable`,
        { contact_id: contactId, reason },
      );
      return res.data;
    },
    onSuccess: () => invalidateContactAccount(qc, contactId),
  });
}

/**
 * `POST /partner/auth/impersonate` — mint an impersonation token and open the
 * partner console in a new tab. No-op (throws) when `APP_PARTNER_URL` is unset.
 */
export function useImpersonateContact(contactId: number) {
  return useMutation({
    mutationFn: async () => {
      const partnerUrl = import.meta.env.APP_PARTNER_URL as string | undefined;
      if (!partnerUrl) throw new Error('Partner console URL is not configured.');
      const res = await dataCentralApi.post<ResponseObject<ImpersonateResult>>(
        `/partner/auth/impersonate`,
        { contact_id: contactId },
      );
      const result = res.data.result;
      if (result?.access_token) {
        const method = result.auth_method === 'local' ? 'local' : 'sso';
        const url =
          `${partnerUrl}/auth/${method}/impersonate` +
          `?uuid=${result.uuid}&exp=${result.exp}&jwt=${result.access_token}`;
        window.open(url, '_blank')?.focus();
      }
      return res.data;
    },
  });
}

/**
 * `PUT /customers/:id/convert` — convert an individual customer into a company
 * account. The backend requires the target `company_id`.
 */
export function useConvertCustomer(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (companyId: number) => {
      const res = await dataCentralApi.put<ResponseObject<unknown>>(
        `/customers/${id}/convert`,
        { company_id: companyId },
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: customerKeys.detail(id) });
      qc.invalidateQueries({ queryKey: customerKeys.all });
    },
  });
}

/** `DELETE /customers/:id` — soft-delete a customer with a reason. */
export function useDeleteCustomer(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (deletionReason: string) => {
      const res = await dataCentralApi.delete<ResponseObject<unknown>>(
        `/customers/${id}`,
        { data: { deletion_reason: deletionReason } },
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: customerKeys.all });
    },
  });
}

/**
 * `PUT /customers/:id/grey-market` — toggle the special-customer (grey-market)
 * flag (invoices sent without the company logo). The endpoint is a pure toggle.
 */
export function useToggleGreyMarket(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await dataCentralApi.put<ResponseObject<unknown>>(
        `/customers/${id}/grey-market`,
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: customerKeys.detail(id) });
      qc.invalidateQueries({ queryKey: basicKeys.contacts(id) });
    },
  });
}

/* ── Lead pipeline actions ─────────────────────────────────────────────── */

export type CustomerLeadType =
  | 'warm-lead'
  | 'sale-qualified-lead'
  | 'cool-lead'
  | 'un-qualified-lead';

/**
 * `PUT /customers/:id/classify-lead` — set the lead type for a cool-lead or
 * qualified-prospect customer. Backend validates `lead_type` against the four
 * allowed values and rejects active customers.
 */
export function useClassifyLeadType(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (leadType: CustomerLeadType) => {
      const res = await dataCentralApi.put<ResponseObject<unknown>>(
        `/customers/${id}/classify-lead`,
        { lead_type: leadType },
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: customerKeys.detail(id) });
      qc.invalidateQueries({ queryKey: ['customers', 'activity-logs', id] });
    },
  });
}

/**
 * `PUT /customers/:id/status` — update customer lifecycle status. Used for
 * "Unfollow Lead" (change to 'dead-lead') and other status transitions.
 * Backend expects `customer_status` (required) and optional `customer_note`.
 */
export function useUpdateCustomerStatus(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { customer_status: string; customer_note?: string }) => {
      const res = await dataCentralApi.put<ResponseObject<unknown>>(
        `/customers/${id}/status`,
        vars,
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: customerKeys.detail(id) });
      qc.invalidateQueries({ queryKey: customerKeys.all });
      qc.invalidateQueries({ queryKey: ['customers', 'activity-logs', id] });
    },
  });
}

/* ── Pinned notes (customer logs) ─────────────────────────────────────── */

/** A pinned customer log row as returned by `GET /customers/:id/logs/pinned`. */
export interface PinnedLogRow {
  customer_log_id: number;
  customer_id: number;
  customer_log_type: string;
  customer_log_description: string | null;
  metadata: Record<string, unknown> | null;
  employee_id: number;
  created_at: string | null;
  is_pinned: boolean | null;
}

export const pinnedLogKeys = {
  pinned: (id: number) => ['customers', 'logs', 'pinned', id] as const,
};

/** `GET /customers/:id/logs/pinned` — all pinned notes for a customer. */
export function useCustomerPinnedLogs(id: number) {
  return useQuery({
    queryKey: pinnedLogKeys.pinned(id),
    enabled: !!id,
    queryFn: async (): Promise<ResponseArray<PinnedLogRow>> => {
      const res = await dataCentralApi.get<ResponseArray<PinnedLogRow>>(
        `/customers/${id}/logs/pinned`,
      );
      return res.data;
    },
  });
}

/** `PUT /customers/:id/logs/:logId/pin` — pin a customer log. */
export function usePinLog(customerId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (logId: number) => {
      const res = await dataCentralApi.put<ResponseObject<unknown>>(
        `/customers/${customerId}/logs/${logId}/pin`,
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pinnedLogKeys.pinned(customerId) });
      qc.invalidateQueries({ queryKey: ['customers', 'activity-logs', customerId] });
    },
  });
}

/** `PUT /customers/:id/logs/:logId/unpin` — unpin a customer log. */
export function useUnpinLog(customerId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (logId: number) => {
      const res = await dataCentralApi.put<ResponseObject<unknown>>(
        `/customers/${customerId}/logs/${logId}/unpin`,
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pinnedLogKeys.pinned(customerId) });
      qc.invalidateQueries({ queryKey: ['customers', 'activity-logs', customerId] });
    },
  });
}

/**
 * `PUT /customers/:id/logs/:logId/note` — update the text of a pinned note log.
 * Payload: `{ note_description, is_pinned }` — both optional per the Go binding.
 */
export function useUpdateNoteLog(customerId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      logId: number;
      note_description?: string;
      is_pinned?: boolean;
    }) => {
      const res = await dataCentralApi.put<ResponseObject<unknown>>(
        `/customers/${customerId}/logs/${vars.logId}/note`,
        { note_description: vars.note_description, is_pinned: vars.is_pinned },
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pinnedLogKeys.pinned(customerId) });
      qc.invalidateQueries({ queryKey: ['customers', 'activity-logs', customerId] });
    },
  });
}
